import React, { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  Checkbox,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  Drawer,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { makeApiCall } from '../../config/api';
import SignaturePad from '../HR/Forms/SignaturePad';

const InspectionWizard = ({ onBack }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const customLocationRef = useRef(null);

  const INSPECTION_SECTIONS = [
    { id: 1, name: t('inspections.sections.airBrakeSystem') },
    { id: 2, name: t('inspections.sections.cab') },
    { id: 3, name: t('inspections.sections.cargoSecurement') },
    { id: 4, name: t('inspections.sections.couplingDevices') },
    { id: 5, name: t('inspections.sections.dangerousGoods') },
    { id: 6, name: t('inspections.sections.driverControls') },
    { id: 7, name: t('inspections.sections.driverSeat') },
    { id: 8, name: t('inspections.sections.electricBrakeSystem') },
    { id: 9, name: t('inspections.sections.emergencyEquipment') },
    { id: 10, name: t('inspections.sections.exhaustSystem') },
    { id: 11, name: t('inspections.sections.frameAndCargoBody') },
    { id: 12, name: t('inspections.sections.fuelSystem') },
    { id: 13, name: t('inspections.sections.general') },
    { id: 14, name: t('inspections.sections.glassAndMirrors') },
    { id: 15, name: t('inspections.sections.heaterDefroster') },
    { id: 16, name: t('inspections.sections.horn') },
    { id: 17, name: t('inspections.sections.hydraulicBrakeSystem') },
    { id: 18, name: t('inspections.sections.lampsAndReflectors') },
    { id: 19, name: t('inspections.sections.steering') },
    { id: 20, name: t('inspections.sections.suspensionSystem') },
    { id: 21, name: t('inspections.sections.tires') },
    { id: 22, name: t('inspections.sections.wheelsHubsFasteners') },
    { id: 23, name: t('inspections.sections.windshieldWiperWasher') }
  ];

  // 'main' = Inspection Main, 1-23 = section ids, 'review' = Review & Submit
  const [activePanel, setActivePanel] = useState('main');
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [inspectionId, setInspectionId] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mainSaved, setMainSaved] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [employeeSignature, setEmployeeSignature] = useState(null);

  const [mainData, setMainData] = useState({
    vehicle_id: null,
    vehicle_name: '',
    vehicle_type_id: 1,
    vehicle_type: 'Vehicle',
    plate_no: '',
    is_trailer_attached: false,
    trailer_id: null,
    trailer_name: '',
    trailer_plate_no: '',
    driver: user?.first_name + ' ' + user?.last_name || '',
    user_id: user?.id,
    odometer: '',
    trip_type_id: 1,
    trip_type: 'Pre-Trip',
    location_id: '1',
    location: '1375 Greenspring Road, Campbellville, ON, L0P 1B0',
    is_custom_location_added: false,
    custom_location: ''
  });

  const [inspectionData, setInspectionData] = useState(() => {
    const data = {};
    INSPECTION_SECTIONS.forEach(section => {
      data[section.id] = {
        action_type_id: 1,
        notes: '',
        touched: false
      };
    });
    return data;
  });

  const [trailerInspectionData, setTrailerInspectionData] = useState(() => {
    const data = {};
    INSPECTION_SECTIONS.forEach(section => {
      data[section.id] = {
        action_type_id: 2,
        notes: '',
        touched: false
      };
    });
    return data;
  });

  const locations = [
    { id: '1', label: t('inspections.locations.greenspring') },
    { id: '2', label: t('inspections.locations.dundas') },
    { id: '3', label: t('inspections.locations.read') },
    { id: '4', label: t('inspections.locations.northQueen') },
    { id: 'custom', label: t('inspections.locations.custom') }
  ];

  const [assignedVehicleId, setAssignedVehicleId] = useState(null);

  useEffect(() => {
    fetchVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVehicles = async () => {
    try {
      const trucksResponse = await makeApiCall('api/routes/trucks?scope=fleet');
      if (trucksResponse.ok) {
        const data = await trucksResponse.json();
        const allTrucks = data.trucks || [];
        const vehiclesList = allTrucks.filter(v =>
          !v.vehicle_trailer_type_id || v.vehicle_trailer_type_id !== 2
        );
        setVehicles(vehiclesList);

        // TKT-447: start on the truck this person is assigned to (driver,
        // then passenger, then extra crew). The full fleet stays in the list
        // for pickups, drop-offs and swaps.
        const uid = Number(user?.id);
        if (uid) {
          const onTruck = (v) => Array.isArray(v.additional_crew)
            && v.additional_crew.some((c) => Number(c.user_id) === uid);
          const assigned = vehiclesList.find((v) => Number(v.current_driver_user_id) === uid)
            || vehiclesList.find((v) => Number(v.current_passenger_user_id) === uid)
            || vehiclesList.find(onTruck);
          if (assigned) {
            setAssignedVehicleId(assigned.id);
            setMainData((prev) => (prev.vehicle_id ? prev : {
              ...prev,
              vehicle_id: assigned.id,
              vehicle_name: assigned.name || assigned.inspection_vehicle_name || `Vehicle ${assigned.id}`,
              plate_no: assigned.plate_number || '',
              vehicle_type_id: assigned.vehicle_trailer_type_id || 1,
              vehicle_type: assigned.vehicle_trailer_type_id === 2 ? 'Trailer' : 'Vehicle',
            }));
          }
        }
      }

      const trailersResponse = await makeApiCall('api/trailers');
      if (trailersResponse.ok) {
        const data = await trailersResponse.json();
        setTrailers(data.trailers || []);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const handleVehicleChange = (vehicle) => {
    if (!vehicle) return;
    setMainData(prev => ({
      ...prev,
      vehicle_id: vehicle.id,
      vehicle_name: vehicle.name || vehicle.inspection_vehicle_name || `Vehicle ${vehicle.id}`,
      plate_no: vehicle.plate_number || '',
      vehicle_type_id: vehicle.vehicle_trailer_type_id || 1,
      vehicle_type: vehicle.vehicle_trailer_type_id === 2 ? 'Trailer' : 'Vehicle'
    }));
  };

  const handleTrailerChange = (trailer) => {
    if (!trailer) {
      setMainData(prev => ({ ...prev, trailer_id: null, trailer_name: '', trailer_plate_no: '' }));
      return;
    }
    setMainData(prev => ({
      ...prev,
      trailer_id: trailer.id,
      trailer_name: trailer.inspection_name || trailer.name || `Trailer ${trailer.id}`,
      trailer_plate_no: trailer.plate_number || ''
    }));
  };

  const updateInspectionSection = (sectionId, actionTypeId, notes) => {
    setInspectionData(prev => ({
      ...prev,
      [sectionId]: {
        action_type_id: actionTypeId,
        notes: notes !== undefined ? notes : prev[sectionId].notes,
        touched: true
      }
    }));
  };

  const updateTrailerInspectionSection = (sectionId, actionTypeId, notes) => {
    setTrailerInspectionData(prev => ({
      ...prev,
      [sectionId]: {
        action_type_id: actionTypeId,
        notes: notes !== undefined ? notes : prev[sectionId].notes,
        touched: true
      }
    }));
  };

  const handleQuickNA = (sectionId) => {
    updateInspectionSection(sectionId, 2);
    if (mainData.is_trailer_attached) {
      updateTrailerInspectionSection(sectionId, 2);
    }
  };

  const handleOpenSection = (panelId) => {
    if (panelId !== 'main' && panelId !== 'review' && !mainSaved) return;
    setActivePanel(panelId);
    if (isMobile) setMobileDrawerOpen(false);
  };

  const handleSaveMainAndContinue = async () => {
    if (!mainData.vehicle_id) return;
    try {
      setLoading(true);
      if (!inspectionId) {
        const response = await makeApiCall('api/inspections', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(mainData)
        });
        if (response.ok) {
          const data = await response.json();
          setInspectionId(data.id);
          setMainSaved(true);
          setActivePanel(1);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || t('inspections.messages.failedToCreateInspection'));
        }
      } else {
        setMainSaved(true);
        setActivePanel(1);
      }
    } catch (error) {
      console.error('Error creating inspection:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveAllFormsAndSubmit = async () => {
    if (!inspectionId) return;
    try {
      setLoading(true);

      for (const section of INSPECTION_SECTIONS) {
        const formData = inspectionData[section.id];
        await makeApiCall(`api/inspections/${inspectionId}/forms`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            form_id: section.id,
            form_name: section.name,
            truck_type_id: 1,
            action_type_id: formData.action_type_id,
            action_type: formData.action_type_id === 1 ? 'OK' : formData.action_type_id === 2 ? 'N/A' : 'Defect',
            form_data: formData
          })
        });

        if (mainData.is_trailer_attached) {
          const trailerFormData = trailerInspectionData[section.id];
          await makeApiCall(`api/inspections/${inspectionId}/forms`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              form_id: section.id,
              form_name: section.name,
              truck_type_id: 2,
              action_type_id: trailerFormData.action_type_id,
              action_type: trailerFormData.action_type_id === 1 ? 'OK' : trailerFormData.action_type_id === 2 ? 'N/A' : 'Defect',
              form_data: trailerFormData
            })
          });
        }
      }

      const hasMajorDefect = Object.values(inspectionData).some(d => d.action_type_id === 3);
      const hasMinorDefect = Object.values(inspectionData).some(d => d.action_type_id === 4);
      const noDefects = !hasMajorDefect && !hasMinorDefect;

      await makeApiCall(`api/inspections/${inspectionId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          review_data: {
            no_vehicle_defect: noDefects,
            minor_vehicle_defect: hasMinorDefect,
            major_vehicle_defect: hasMajorDefect,
            employee_signature: employeeSignature,
            employee_sign_date: employeeSignature ? new Date().toISOString() : null,
            employee_signed_name: mainData.driver || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username || ''
          }
        })
      });

      if (onBack) onBack();
    } catch (error) {
      console.error('Error submitting inspection:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSectionStatus = (sectionId) => {
    const data = inspectionData[sectionId];
    if (!data.touched) return 'pending';
    if (data.action_type_id === 1) return 'ok';
    if (data.action_type_id === 2) return 'na';
    if (data.action_type_id === 3) return 'major';
    if (data.action_type_id === 4) return 'minor';
    return 'pending';
  };

  const getStatusIcon = (sectionId) => {
    const status = getSectionStatus(sectionId);
    switch (status) {
      case 'ok': return <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />;
      case 'na': return <BlockIcon sx={{ fontSize: 18, color: 'text.disabled' }} />;
      case 'major': return <ErrorIcon sx={{ fontSize: 18, color: 'error.main' }} />;
      case 'minor': return <WarningIcon sx={{ fontSize: 18, color: 'warning.main' }} />;
      default: return <UncheckedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />;
    }
  };

  const getStatusChip = (sectionId) => {
    const status = getSectionStatus(sectionId);
    const chipProps = {
      ok: { label: t('inspections.statusLabels.ok'), bgcolor: '#2e7d32', color: '#fff' },
      na: { label: t('inspections.statusLabels.na'), bgcolor: 'grey.500', color: '#fff' },
      major: { label: t('inspections.statusLabels.majorDefect'), bgcolor: '#d32f2f', color: '#fff' },
      minor: { label: t('inspections.statusLabels.minorDefect'), bgcolor: '#ed6c02', color: '#fff' },
      pending: { label: '', bgcolor: 'transparent', color: 'transparent' }
    };
    const props = chipProps[status] || chipProps.pending;
    if (status === 'pending') return null;
    return (
      <Chip
        label={props.label}
        size="small"
        sx={{
          bgcolor: props.bgcolor,
          color: props.color,
          fontWeight: 600,
          fontSize: '0.65rem',
          height: 22,
          minWidth: 32
        }}
      />
    );
  };

  const completedCount = Object.values(inspectionData).filter(d => d.touched).length;
  const totalSections = INSPECTION_SECTIONS.length;

  const sidebarContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Sidebar header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
        color: '#fff',
        py: 2,
        px: 2,
        textAlign: 'center',
        borderRadius: isMobile ? 0 : '8px 8px 0 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }} aria-label={t('inspections.title')}>
          {t('inspections.sidebar.vehicleInspection')}
        </Typography>
        {isMobile && (
          <IconButton onClick={() => setMobileDrawerOpen(false)} sx={{ color: '#fff' }} aria-label={t('common.close')}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {mainSaved && (
        <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider', textAlign: 'left' }}>
          <Typography variant="caption" color="text.secondary">
            {t('inspections.sidebar.completed', { count: completedCount, total: totalSections })}
          </Typography>
          <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, height: 6, mt: 0.5 }}>
            <Box sx={{
              width: `${(completedCount / totalSections) * 100}%`,
              bgcolor: completedCount === totalSections ? 'success.main' : 'primary.main',
              borderRadius: 1,
              height: '100%',
              transition: 'width 0.3s ease'
            }} />
          </Box>
        </Box>
      )}

      {/* Sections list */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1, textAlign: 'left' }}>
        {/* Inspection Main */}
        <Box
          onClick={() => handleOpenSection('main')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            pl: 1,
            pr: 1.5,
            py: 1.2,
            cursor: 'pointer',
            bgcolor: activePanel === 'main' ? 'rgba(21,101,192,0.08)' : 'transparent',
            borderLeft: activePanel === 'main' ? '4px solid' : '4px solid transparent',
            borderColor: activePanel === 'main' ? 'primary.main' : 'transparent',
            '&:hover': { bgcolor: 'grey.100' }
          }}
          role="button"
          tabIndex={0}
          aria-label={t('inspections.sidebar.inspectionMain')}
          aria-current={activePanel === 'main' ? 'true' : 'false'}
        >
          <Box sx={{ width: 28, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
            {mainSaved ? (
              <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
            ) : (
              <Typography variant="body2" fontWeight="bold" color="primary">**</Typography>
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{ flex: 1, fontWeight: activePanel === 'main' ? 700 : 500, ml: 1, textAlign: 'left' }}
          >
            {t('inspections.sidebar.inspectionMain')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, ml: 1 }}>
            <Chip
              label={t('inspections.sidebar.open')}
              size="small"
              sx={{
                bgcolor: activePanel === 'main' ? 'primary.main' : '#e67e22',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 22,
                cursor: 'pointer'
              }}
            />
          </Box>
        </Box>

        {/* Numbered inspection sections */}
        {INSPECTION_SECTIONS.map((section, idx) => {
          const status = getSectionStatus(section.id);
          const isActive = activePanel === section.id;
          const isDisabled = !mainSaved;
          const statusChip = getStatusChip(section.id);

          return (
            <Box
              key={section.id}
              onClick={() => { if (!isDisabled) handleOpenSection(section.id); }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                pl: 1,
                pr: 1.5,
                py: 0.8,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.5 : 1,
                bgcolor: isActive ? 'rgba(21,101,192,0.08)' : 'transparent',
                borderLeft: isActive ? '4px solid' : '4px solid transparent',
                borderColor: isActive ? 'primary.main' : 'transparent',
                '&:hover': isDisabled ? {} : { bgcolor: 'grey.100' }
              }}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              aria-label={`${idx + 1}. ${section.name}`}
            >
              <Box sx={{ width: 28, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                {getStatusIcon(section.id)}
              </Box>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  flex: 1,
                  fontWeight: isActive ? 700 : 400,
                  fontSize: '0.82rem',
                  color: isDisabled ? 'text.disabled' : 'text.primary',
                  ml: 1,
                  textAlign: 'left'
                }}
              >
                {idx + 1}. {section.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, ml: 1, alignItems: 'center' }}>
                {statusChip}
                {status === 'pending' && (
                  <Chip
                    label={t('inspections.statusLabels.na')}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isDisabled) handleQuickNA(section.id);
                    }}
                    sx={{
                      bgcolor: 'grey.300',
                      color: 'text.secondary',
                      fontWeight: 600,
                      fontSize: '0.65rem',
                      height: 22,
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      '&:hover': isDisabled ? {} : { bgcolor: 'grey.400' }
                    }}
                    aria-label={`${t('inspections.statusLabels.na')} - ${section.name}`}
                  />
                )}
                <Chip
                  label={t('inspections.sidebar.open')}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isDisabled) handleOpenSection(section.id);
                  }}
                  sx={{
                    bgcolor: isActive ? 'primary.main' : '#e67e22',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    height: 22,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    '&:hover': isDisabled ? {} : { bgcolor: isActive ? 'primary.dark' : '#d35400' }
                  }}
                  aria-label={`${t('inspections.sidebar.open')} - ${section.name}`}
                />
              </Box>
            </Box>
          );
        })}

        {/* Review & Submit */}
        <Box
          onClick={() => mainSaved && handleOpenSection('review')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            pl: 1,
            pr: 1.5,
            py: 1.2,
            cursor: mainSaved ? 'pointer' : 'not-allowed',
            opacity: mainSaved ? 1 : 0.5,
            bgcolor: activePanel === 'review' ? 'rgba(21,101,192,0.08)' : 'transparent',
            borderLeft: activePanel === 'review' ? '4px solid' : '4px solid transparent',
            borderColor: activePanel === 'review' ? 'primary.main' : 'transparent',
            '&:hover': mainSaved ? { bgcolor: 'grey.100' } : {},
            mt: 1,
            borderTop: '1px solid',
            borderTopColor: 'divider',
            pt: 1.5
          }}
          role="button"
          tabIndex={0}
          aria-label={t('inspections.reviewAndSubmit')}
          aria-current={activePanel === 'review' ? 'true' : 'false'}
        >
          <Box sx={{ width: 28, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
            <Typography variant="body2" fontWeight="bold" color="primary">**</Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{ flex: 1, fontWeight: activePanel === 'review' ? 700 : 500, ml: 1, textAlign: 'left' }}
          >
            {t('inspections.reviewAndSubmit')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, ml: 1 }}>
            <Chip
              label={t('inspections.sidebar.open')}
              size="small"
              sx={{
                bgcolor: activePanel === 'review' ? 'primary.main' : '#e67e22',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 22,
                cursor: mainSaved ? 'pointer' : 'not-allowed'
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Cancel button at bottom of sidebar */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          fullWidth
          variant="outlined"
          color="error"
          onClick={() => setConfirmDialogOpen(true)}
          aria-label={t('common.cancel')}
        >
          {t('common.cancel')}
        </Button>
      </Box>
    </Box>
  );

  const renderMainPanel = () => (
    <Box>
      <Grid container spacing={3}>
        <Grid size={12}>
          <Typography variant="h6" gutterBottom>
            {t('inspections.vehicleInformation')}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Autocomplete
            fullWidth
            options={vehicles}
            value={vehicles.find((v) => v.id === mainData.vehicle_id) || null}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.name || option.inspection_vehicle_name || `Vehicle ${option.id}`}
            onChange={(e, value) => handleVehicleChange(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('inspections.selectVehicle')}
                required
                placeholder={t('inspections.selectVehiclePlaceholder')}
                helperText={assignedVehicleId && mainData.vehicle_id === assignedVehicleId
                  ? t('inspections.assignedTruckPreselected')
                  : undefined}
              />
            )}
            sx={{ minWidth: 200 }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={t('inspections.plateNumber')}
            value={mainData.plate_no}
            InputProps={{ readOnly: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={t('inspections.driverName')}
            value={mainData.driver}
            onChange={(e) => setMainData({ ...mainData, driver: e.target.value })}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={t('inspections.odometerReading')}
            type="number"
            value={mainData.odometer}
            onChange={(e) => setMainData({ ...mainData, odometer: e.target.value })}
            placeholder={t('inspections.sidebar.enterOdometer')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            select
            label={t('inspections.location') + ' *'}
            value={mainData.is_custom_location_added ? 'custom' : mainData.location_id}
            slotProps={{
              // Let the custom address box keep focus when the menu closes
              select: { MenuProps: { disableRestoreFocus: true, disableEnforceFocus: true } }
            }}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'custom') {
                // Render the address box now and focus it while still inside the tap,
                // otherwise phones (iOS especially) don't open the keyboard
                flushSync(() => {
                  setMainData({ ...mainData, is_custom_location_added: true, location_id: '', location: '' });
                });
                customLocationRef.current?.focus();
              } else {
                const location = locations.find(l => l.id === value);
                setMainData({ ...mainData, is_custom_location_added: false, location_id: value, location: location.label });
              }
            }}
          >
            {locations.map(loc => (
              <MenuItem key={loc.id} value={loc.id}>{loc.label}</MenuItem>
            ))}
          </TextField>
          {mainData.is_custom_location_added && (
            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label={t('inspections.customLocation')}
              value={mainData.custom_location}
              inputRef={customLocationRef}
              autoComplete="street-address"
              onChange={(e) => setMainData({ ...mainData, custom_location: e.target.value })}
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('inspections.tripType')}:
            </Typography>
            <ToggleButtonGroup
              value={mainData.trip_type_id}
              exclusive
              onChange={(e, val) => {
                if (val !== null) {
                  setMainData({
                    ...mainData,
                    trip_type_id: val,
                    trip_type: val === 1 ? t('inspections.tripTypes.preTrip') : t('inspections.tripTypes.postTrip')
                  });
                }
              }}
              aria-label={t('inspections.tripType')}
              size="small"
              fullWidth
            >
              <ToggleButton
                value={1}
                sx={{
                  fontWeight: 600,
                  '&.Mui-selected': { bgcolor: '#e67e22', color: '#fff', '&:hover': { bgcolor: '#d35400' } }
                }}
              >
                {t('inspections.tripTypes.preTrip')}
              </ToggleButton>
              <ToggleButton
                value={2}
                sx={{
                  fontWeight: 600,
                  '&.Mui-selected': { bgcolor: '#e67e22', color: '#fff', '&:hover': { bgcolor: '#d35400' } }
                }}
              >
                {t('inspections.tripTypes.postTrip')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Grid>

        {/* Trailers section */}
        <Grid size={12}>
          <Box sx={{
            background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
            color: '#fff',
            py: 1,
            px: 2,
            borderRadius: 1,
            textAlign: 'center',
            mt: 1
          }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {t('inspections.sidebar.trailers')}
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">
              {t('inspections.trailerAttached')}
            </Typography>
            <Switch
              checked={mainData.is_trailer_attached}
              onChange={(e) => setMainData({ ...mainData, is_trailer_attached: e.target.checked })}
              aria-label={t('inspections.trailerAttached')}
            />
          </Box>
        </Grid>
        {mainData.is_trailer_attached && (
          <>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                fullWidth
                options={trailers}
                getOptionLabel={(option) => option.inspection_name || option.name || `Trailer ${option.id}`}
                onChange={(e, value) => handleTrailerChange(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('inspections.selectTrailer')}
                    placeholder={t('inspections.selectTrailerPlaceholder')}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('inspections.sidebar.trailerPlate')}
                value={mainData.trailer_plate_no}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </>
        )}

        <Grid size={12}>
          <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{t('inspections.sidebar.schedule')}:</strong>{' '}
              {t('inspections.sidebar.scheduleDescription')}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSaveMainAndContinue}
          disabled={loading || !mainData.vehicle_id}
          sx={{
            px: 4,
            py: 1.2,
            fontWeight: 700,
            bgcolor: '#1565c0',
            '&:hover': { bgcolor: '#0d47a1' }
          }}
          aria-label={t('common.next')}
        >
          {loading ? <CircularProgress size={24} /> : t('common.next')}
        </Button>
      </Box>
    </Box>
  );

  const renderSectionPanel = (sectionId) => {
    const section = INSPECTION_SECTIONS.find(s => s.id === sectionId);
    if (!section) return null;

    const data = inspectionData[sectionId];
    const sectionIdx = INSPECTION_SECTIONS.findIndex(s => s.id === sectionId);

    const renderSectionForm = (isTrailer = false) => {
      const formData = isTrailer ? trailerInspectionData[sectionId] : data;
      const updateFn = isTrailer ? updateTrailerInspectionSection : updateInspectionSection;

      return (
        <Box sx={{ mb: isTrailer ? 0 : 3 }}>
          {isTrailer && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {t('inspections.guidance.trailerInspectionAlert')} - {mainData.trailer_name}
              </Typography>
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isTrailer ? t('inspections.guidance.trailerInspectionSelect') : t('inspections.guidance.vehicleInspectionSelect')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
            <Button
              variant={formData.action_type_id === 1 ? 'contained' : 'outlined'}
              color="success"
              onClick={() => updateFn(sectionId, 1)}
              sx={{ minWidth: 90, fontWeight: 600 }}
              aria-label={t('inspections.statusLabels.ok')}
            >
              {t('inspections.statusLabels.ok')}
            </Button>
            <Button
              variant={formData.action_type_id === 2 ? 'contained' : 'outlined'}
              color="inherit"
              onClick={() => updateFn(sectionId, 2)}
              sx={{ minWidth: 90, fontWeight: 600 }}
              aria-label={t('inspections.statusLabels.na')}
            >
              {t('inspections.statusLabels.na')}
            </Button>
            <Button
              variant={formData.action_type_id === 4 ? 'contained' : 'outlined'}
              color="warning"
              onClick={() => updateFn(sectionId, 4)}
              sx={{ minWidth: 130, fontWeight: 600 }}
              aria-label={t('inspections.statusLabels.minorDefect')}
            >
              {t('inspections.statusLabels.minorDefect')}
            </Button>
            <Button
              variant={formData.action_type_id === 3 ? 'contained' : 'outlined'}
              color="error"
              onClick={() => updateFn(sectionId, 3)}
              sx={{ minWidth: 130, fontWeight: 600 }}
              aria-label={t('inspections.statusLabels.majorDefect')}
            >
              {t('inspections.statusLabels.majorDefect')}
            </Button>
          </Box>

          {(formData.action_type_id === 3 || formData.action_type_id === 4) && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('inspections.labels.notes')}
              value={formData.notes}
              onChange={(e) => updateFn(sectionId, formData.action_type_id, e.target.value)}
              placeholder={t('inspections.labels.notesDescription')}
            />
          )}
        </Box>
      );
    };

    const goToNext = () => {
      if (sectionIdx < INSPECTION_SECTIONS.length - 1) {
        setActivePanel(INSPECTION_SECTIONS[sectionIdx + 1].id);
      } else {
        setActivePanel('review');
      }
    };

    const goToPrev = () => {
      if (sectionIdx > 0) {
        setActivePanel(INSPECTION_SECTIONS[sectionIdx - 1].id);
      } else {
        setActivePanel('main');
      }
    };

    return (
      <Box>
        {renderSectionForm(false)}
        {mainData.is_trailer_attached && (
          <Box sx={{ mt: 3, pt: 3, borderTop: 2, borderColor: 'divider' }}>
            {renderSectionForm(true)}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button variant="outlined" onClick={goToPrev} aria-label={t('common.back')}>
            {t('common.back')}
          </Button>
          <Button
            variant="contained"
            onClick={goToNext}
            sx={{ fontWeight: 600, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
            aria-label={t('common.next')}
          >
            {t('common.next')}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderReviewPanel = () => {
    const hasMajorDefect = Object.values(inspectionData).some(d => d.action_type_id === 3);
    const hasMinorDefect = Object.values(inspectionData).some(d => d.action_type_id === 4);

    return (
      <Box>
        <Alert severity={hasMajorDefect ? 'error' : hasMinorDefect ? 'warning' : 'success'} sx={{ mb: 3 }}>
          {hasMajorDefect && t('inspections.alerts.majorDefectsFound')}
          {!hasMajorDefect && hasMinorDefect && t('inspections.alerts.minorDefectsFound')}
          {!hasMajorDefect && !hasMinorDefect && t('inspections.alerts.noDefectsFound')}
        </Alert>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('inspections.vehicleInformation')}
            </Typography>
            <Grid container spacing={1}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="body2" color="text.secondary">{t('inspections.vehicle')}:</Typography>
                <Typography variant="body2" fontWeight={500}>{mainData.vehicle_name}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="body2" color="text.secondary">{t('inspections.plateNumber')}:</Typography>
                <Typography variant="body2" fontWeight={500}>{mainData.plate_no}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="body2" color="text.secondary">{t('inspections.driver')}:</Typography>
                <Typography variant="body2" fontWeight={500}>{mainData.driver}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="body2" color="text.secondary">{t('inspections.type')}:</Typography>
                <Typography variant="body2" fontWeight={500}>{mainData.trip_type}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="body2" color="text.secondary">{t('inspections.odometer')}:</Typography>
                <Typography variant="body2" fontWeight={500}>{mainData.odometer || '—'}</Typography>
              </Grid>
              {mainData.is_trailer_attached && (
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('inspections.trailer')}:</Typography>
                  <Typography variant="body2" fontWeight={500}>{mainData.trailer_name}</Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('inspections.inspectionResultsSummary')}
            </Typography>
            <Grid container spacing={1}>
              {INSPECTION_SECTIONS.map(section => {
                const sData = inspectionData[section.id];
                const statusColor =
                  sData.action_type_id === 1 ? 'success.main' :
                  sData.action_type_id === 2 ? 'text.secondary' :
                  sData.action_type_id === 3 ? 'error.main' : 'warning.main';
                const statusText =
                  sData.action_type_id === 1 ? t('inspections.statusLabels.ok') :
                  sData.action_type_id === 2 ? t('inspections.statusLabels.na') :
                  sData.action_type_id === 3 ? t('inspections.statusLabels.majorDefect') : t('inspections.statusLabels.minorDefect');

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={section.id}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        py: 0.5,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'grey.50' },
                        px: 1,
                        borderRadius: 1
                      }}
                      onClick={() => setActivePanel(section.id)}
                      role="button"
                      aria-label={`${section.name}: ${statusText}`}
                    >
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{section.name}</Typography>
                      <Typography variant="body2" fontWeight="bold" color={statusColor} sx={{ fontSize: '0.8rem' }}>
                        {statusText}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('inspections.employeeSignature', 'Employee Signature')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('inspections.signatureAcknowledgement', 'Sign below to acknowledge that this inspection is complete and accurate.')}
            </Typography>
            <SignaturePad
              onSignature={setEmployeeSignature}
              label={t('inspections.employeeSignature', 'Employee signature')}
              language={(i18n.language || 'en').startsWith('es') ? 'es' : 'en'}
            />
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="large"
            onClick={saveAllFormsAndSubmit}
            disabled={loading || !employeeSignature}
            sx={{
              px: 4,
              py: 1.2,
              fontWeight: 700,
              bgcolor: '#2e7d32',
              '&:hover': { bgcolor: '#1b5e20' }
            }}
            aria-label={`${t('common.submit')} ${t('inspections.title')}`}
          >
            {loading ? <CircularProgress size={24} /> : `${t('common.submit')} ${t('inspections.title')}`}
          </Button>
        </Box>
      </Box>
    );
  };

  const getPanelTitle = () => {
    if (activePanel === 'main') return t('inspections.sidebar.inspectionMain');
    if (activePanel === 'review') return t('inspections.reviewAndSubmit');
    const section = INSPECTION_SECTIONS.find(s => s.id === activePanel);
    return section ? section.name : '';
  };

  const contentPanel = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{
        background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
        color: '#fff',
        py: 2,
        px: 3,
        borderRadius: isMobile ? 0 : '8px 8px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        {isMobile && (
          <IconButton onClick={() => setMobileDrawerOpen(true)} sx={{ color: '#fff' }} aria-label={t('inspections.sidebar.openSections')}>
            <MenuIcon />
          </IconButton>
        )}
        <Typography variant="h6" fontWeight="bold" aria-label={getPanelTitle()}>
          {getPanelTitle()}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        {activePanel === 'main' && renderMainPanel()}
        {activePanel === 'review' && renderReviewPanel()}
        {typeof activePanel === 'number' && renderSectionPanel(activePanel)}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      {/* Desktop layout: sidebar + content */}
      {!isMobile ? (
        <Box sx={{ display: 'flex', flex: 1, gap: 2, overflow: 'hidden' }}>
          <Card sx={{
            width: 380,
            minWidth: 340,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 2
          }}>
            {sidebarContent}
          </Card>
          <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 2 }}>
            {contentPanel}
          </Card>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Drawer
            anchor="left"
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
            PaperProps={{ sx: { width: '85%', maxWidth: 360 } }}
          >
            {sidebarContent}
          </Drawer>
          <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 0 }}>
            {contentPanel}
          </Card>
        </Box>
      )}

      {/* Cancel confirmation dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="cancel-dialog-title"
      >
        <DialogTitle id="cancel-dialog-title">
          {t('inspections.sidebar.cancelTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('inspections.sidebar.cancelMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} aria-label={t('inspections.sidebar.keepWorking')}>
            {t('inspections.sidebar.keepWorking')}
          </Button>
          <Button
            onClick={() => { setConfirmDialogOpen(false); if (onBack) onBack(); }}
            color="error"
            variant="contained"
            aria-label={t('inspections.sidebar.discardExit')}
          >
            {t('inspections.sidebar.discardExit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InspectionWizard;
